/**
 * Componente: Tarjeta visual de licencia (ISO/IEC 7810 ID-1)
 * Presentacional puro: recibe los datos del contribuyente y el diseño de
 * marca del municipio (logo + colores) y solo dibuja la tarjeta. Lo usan
 * tanto la Vista Previa del Padrón como el live preview del Diseñador.
 *
 * Cada cara (frente/dorso) se resuelve por separado, en 3 casos:
 *  (a) hay elementos posicionados (layout_frente/layout_dorso) -> se dibujan
 *      esos, sobre el fondo (color/gradiente/imagen). El logo del municipio
 *      es un elemento de imagen más del layout, no un overlay automático.
 *  (b) sin elementos pero con imagen de fondo cargada -> se muestra/imprime
 *      SOLO la imagen, nada superpuesto (diseño válido a propósito).
 *  (c) sin elementos y sin imagen -> no hay nada configurado: en impresión
 *      la cara sale en blanco; en pantalla se muestra un aviso claro (NO una
 *      licencia de ejemplo con datos falsos).
 *
 * fondo_imagen_frente_url/fondo_imagen_dorso_url reemplazan el fondo del
 * card (color/gradiente) por una imagen subida, en dos casos: tarjeta
 * "preimpresa" (siempre, es solo para previsualizar en pantalla — ver la
 * nota de regla de negocio crítica en el backend, resolverDisenoIndividual())
 * o tarjeta "blanca" con tipo_fondo="imagen_personalizada" (ahí sí es el
 * fondo real de impresión).
 *
 * Campo "clases": por defecto (y en todos los diseños ya guardados) imprime solo los códigos
 * ("B1, B2"). Con formato_clases = 'codigo_descripcion' imprime una clase por línea con su descripción
 * (la del nomenclador del municipio), achicando el texto hasta un mínimo y cortándolo con "…" si no entra
 * (ver utils/formatoClases.js: la misma cuenta para la vista previa, el Diseñador y la impresión).
 *
 * Elementos "qr": el contenido se arma según contenido_tipo (catálogo de
 * opciones, ver RESOLVERES_CONTENIDO_QR) — hoy solo "datos_plano" (DNI +
 * nombre + vencimiento del contribuyente real).
 */

import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { formatearFecha, formatearCuil } from '../utils/fecha';
import { IconPersona, IconAdvertencia } from './icons';
import { useClasesMunicipio } from '../utils/clasesLicencia';
import {
  FORMATO_CODIGO_DESCRIPCION,
  formatoDeElemento,
  resolverClasesCodigoDescripcion,
  pxDisenoACqw,
  textoClasesSoloCodigos,
  useTipografiasListas,
} from '../utils/formatoClases';

const TIPO_TRAMITE_LABELS = {
  nueva: 'Nueva',
  renovacion: 'Renovación',
  duplicado: 'Duplicado',
  ampliacion: 'Ampliación',
};

// Calle + número como un solo string ("SAN MARTÍN 1234"). Se hace .trim()
// de cada parte porque hay datos ya cargados con espacio sobrante (ej.
// "BAIBIENE ") — sin eso el join dejaba doble espacio ("BAIBIENE  1911").
const resolverCalleNumero = (contribuyente) =>
  [contribuyente.domicilio_calle, contribuyente.domicilio_numero]
    .map((parte) => (parte || '').trim())
    .filter(Boolean)
    .join(' ');

// Calle+número, localidad y provincia son campos separados en la tabla —
// acá se arman en una sola línea (formato típico de licencia), filtrando
// los que falten en vez de dejar comas colgadas.
const resolverDomicilioCompleto = (contribuyente) => {
  const partes = [
    resolverCalleNumero(contribuyente),
    (contribuyente.domicilio_localidad || '').trim(),
    (contribuyente.domicilio_provincia || '').trim(),
  ].filter(Boolean);
  return partes.join(', ') || '-';
};

// Debe coincidir con el ancho de diseño del lienzo en DisenadorEditor.jsx
// (.canvas-lienzo tiene max-width: 700px). tamano_fuente se guarda como
// px absolutos calibrados contra ese ancho, así que acá hay que expresarlo
// como % del ancho de ESTA tarjeta (que puede medir cualquier cosa según
// dónde se renderice) para que el texto se vea proporcionalmente igual.
const CANVAS_ANCHO_DISENO_PX = 700;

const ALINEACION_A_TEXT_ALIGN = { izquierda: 'left', centro: 'center', derecha: 'right' };

// Mismo catálogo de contenido_tipo que DisenadorEditor.jsx (ver el
// comentario ahí sobre por qué es un catálogo y no un switch suelto).
// Duplicado a propósito, no importado: DisenadorEditor.jsx resuelve contra
// CONTRIBUYENTE_EJEMPLO (de vista previa) y acá se resuelve contra el
// contribuyente real, mismo criterio que ya separa resolverTextoPreview de
// resolverValorLayout entre los dos archivos.
const RESOLVERES_CONTENIDO_QR = {
  datos_plano: (contribuyente) =>
    `${(contribuyente.tipo_documento || 'DNI').toUpperCase()}:${contribuyente.numero_documento}|NOMBRE:${contribuyente.apellido} ${contribuyente.nombre}|VENC:${formatearFecha(contribuyente.fecha_vencimiento)}`,
};

const resolverContenidoQR = (elemento, contribuyente) => {
  const resolver = RESOLVERES_CONTENIDO_QR[elemento.contenido_tipo];
  return resolver ? resolver(contribuyente) : '';
};

const resolverValorLayout = (elemento, contribuyente) => {
  if (elemento.fuente_dato === 'texto_fijo') return elemento.texto_fijo || '';
  switch (elemento.fuente_dato) {
    case 'nombre': return `${contribuyente.apellido}, ${contribuyente.nombre}`;
    case 'apellido': return contribuyente.apellido;
    case 'nombre_solo': return contribuyente.nombre;
    case 'dni': return `${contribuyente.tipo_documento} ${contribuyente.numero_documento}`;
    case 'numero_documento': return contribuyente.numero_documento;
    case 'cuit_cuil': return formatearCuil(contribuyente.cuit_cuil) || '-';
    case 'fecha_nacimiento': return formatearFecha(contribuyente.fecha_nacimiento);
    case 'nacionalidad': return contribuyente.nacionalidad || '-';
    case 'sexo': return contribuyente.sexo || '-';
    case 'domicilio': return resolverDomicilioCompleto(contribuyente);
    case 'domicilio_calle_numero': return resolverCalleNumero(contribuyente) || '-';
    case 'domicilio_localidad': return contribuyente.domicilio_localidad || '-';
    case 'domicilio_provincia': return contribuyente.domicilio_provincia || '-';
    case 'domicilio_cp': return contribuyente.domicilio_cp || '-';
    case 'telefono': return contribuyente.telefono || '-';
    case 'email': return contribuyente.email || '-';
    case 'vencimiento': return formatearFecha(contribuyente.fecha_vencimiento);
    case 'emision': return formatearFecha(contribuyente.fecha_emision);
    case 'clases': return textoClasesSoloCodigos(contribuyente.clases);
    case 'grupo_sanguineo': return contribuyente.grupo_sanguineo || '-';
    case 'donante_organos': return contribuyente.donante_organos ? 'Sí' : 'No';
    case 'usa_lentes': return contribuyente.usa_lentes ? 'Sí' : 'No';
    case 'observaciones_medicas': return contribuyente.observaciones_medicas || '-';
    case 'tipo_tramite': return TIPO_TRAMITE_LABELS[contribuyente.tipo_tramite] || contribuyente.tipo_tramite || '-';
    case 'numero_licencia': return contribuyente.numero_licencia || '-';
    case 'numero_tramite': return contribuyente.numero_tramite || '-';
    default: return '';
  }
};

// Campo "clases" en formato 'codigo_descripcion': una clase por línea, achicado para entrar en la caja.
const TextoClasesConDescripcion = ({ elemento, clases, catalogo, estilo }) => {
  const { lineas, fontPx } = resolverClasesCodigoDescripcion({ clases, catalogo, elemento });
  return (
    <span
      className="license-layout-elemento-texto license-layout-elemento-clases"
      style={{ ...estilo, fontSize: pxDisenoACqw(fontPx) }}
    >
      {lineas.join('\n')}
    </span>
  );
};

const LicenseCard = ({ contribuyente, diseno, cara = 'frente', paraImpresionFisica = false }) => {
  // La lista de clases del municipio (con sus descripciones) y las tipografías: se piden/esperan una sola vez
  // por sesión; sirven solo al formato "código + descripción" del campo clases.
  const { catalogo } = useClasesMunicipio();
  useTipografiasListas();

  // El header de la plantilla genérica sigue usando color/gradiente siempre
  // (nunca la imagen de fondo) — no tiene sentido mostrar un recorte de la
  // imagen en una franja de 40px, así que "tipo_fondo=imagen_personalizada"
  // cae acá al mismo tratamiento que "gradiente".
  const fondoCard = diseno.tipo_fondo === 'solido'
    ? diseno.color_primario
    : `linear-gradient(135deg, ${diseno.color_primario}, ${diseno.color_secundario})`;

  // Fondo de imagen por cara: tarjeta preimpresa (solo en pantalla — ver la
  // nota de "regla de negocio crítica" en el backend,
  // resolverDisenoIndividual()) o tarjeta blanca con "Imagen personalizada"
  // elegida como tipo de fondo (esta sí es fondo real, se imprime siempre).
  // paraImpresionFisica=true es la salida que termina en un PDF/impresora
  // real: ahí la preimpresa NUNCA se pinta (la tarjeta física ya la trae
  // de fábrica — pintarla de nuevo sería una doble impresión superpuesta).
  const imagenFondoUrl = cara === 'dorso' ? diseno.fondo_imagen_dorso_url : diseno.fondo_imagen_frente_url;
  const usaFondoImagen = paraImpresionFisica
    ? (diseno.tipo_fondo === 'imagen_personalizada' && imagenFondoUrl)
    : ((diseno.tipo_tarjeta === 'preimpresa' || diseno.tipo_fondo === 'imagen_personalizada') && imagenFondoUrl);
  // Preimpresa + impresión física + sin imagen cargada: tampoco corresponde
  // pintar el degradé/color de respaldo (fondoCard) — la tarjeta física ya
  // viene en blanco con su diseño de fábrica, y pintar CUALQUIER fondo acá
  // (imagen o color) se superpondría a eso. Solo en pantalla se sigue
  // usando fondoCard como respaldo, para que la vista previa no se vea
  // vacía mientras se posicionan los elementos en el canvas.
  const noPintarFondoEnImpresion = paraImpresionFisica && diseno.tipo_tarjeta === 'preimpresa' && !usaFondoImagen;
  const estiloFondoCard = usaFondoImagen
    ? { backgroundImage: `url(${imagenFondoUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : noPintarFondoEnImpresion
      ? {}
      : { background: fondoCard };

  const layoutActivo = cara === 'dorso' ? diseno.layout_dorso : diseno.layout_frente;
  const tieneLayout = Array.isArray(layoutActivo) && layoutActivo.length > 0;
  const tieneImagenFondo = !!imagenFondoUrl;

  // Tres casos por cara, independientes entre frente y dorso:
  //  (a) hay elementos posicionados -> se respeta el layout (rama de abajo).
  //  (b) sin elementos pero con imagen de fondo cargada -> es la imagen sola,
  //      nada superpuesto. estiloFondoCard ya distingue "blanca + imagen
  //      personalizada" (se imprime) de "preimpresa" (solo pantalla, blanco
  //      en impresión).
  //  (c) sin elementos y sin imagen -> no hay nada configurado. En impresión:
  //      cara en blanco. En pantalla: aviso inconfundible (NUNCA una licencia
  //      de ejemplo con datos falsos, que se leía como diseño terminado).

  if (!tieneLayout && tieneImagenFondo) {
    return <div className="license-card license-card-solo-fondo" style={estiloFondoCard} />;
  }

  if (!tieneLayout) {
    if (paraImpresionFisica) {
      return <div className="license-card" />;
    }
    return (
      <div className="license-card license-card-sin-diseno">
        <IconAdvertencia size="1.5em" strokeWidth={2} />
        <p className="license-sin-diseno-titulo">Esta cara no tiene diseño configurado</p>
        <p className="license-sin-diseno-sub">
          Agregá elementos o una imagen de fondo en el Diseñador.
        </p>
      </div>
    );
  }

  if (tieneLayout) {
    return (
      <div className="license-card license-card-layout" style={estiloFondoCard}>
        {layoutActivo.map((elemento) => (
          <div
            key={elemento.id}
            className="license-layout-elemento"
            style={{
              left: `${elemento.x}%`,
              top: `${elemento.y}%`,
              width: `${elemento.width}%`,
              height: `${elemento.height}%`,
              transform: `rotate(${elemento.rotacion || 0}deg)`,
              ...(elemento.tipo === 'texto' ? {
                fontSize: `${(elemento.tamano_fuente / CANVAS_ANCHO_DISENO_PX) * 100}cqw`,
                justifyContent: elemento.alineacion === 'centro' ? 'center' : elemento.alineacion === 'derecha' ? 'flex-end' : 'flex-start',
                // El texto se ancla al borde SUPERIOR de la caja y crece hacia
                // abajo (2+ líneas si el ancho no alcanza) — mismo criterio que
                // el canvas del Diseñador. Los no-texto siguen centrados (CSS).
                alignItems: 'flex-start',
              } : { justifyContent: 'center' }),
            }}
          >
            {elemento.tipo === 'imagen' ? (
              elemento.fuente_imagen === 'logo_municipio' ? (
                diseno.logo_url && <img src={diseno.logo_url} alt="" className="license-layout-elemento-img" />
              ) : contribuyente.foto_url ? (
                <img src={contribuyente.foto_url} alt="" className="license-layout-elemento-img license-layout-elemento-img-foto" />
              ) : (
                <span className="license-layout-elemento-placeholder"><IconPersona size="1em" strokeWidth={2} /></span>
              )
            ) : elemento.tipo === 'linea' ? (
              <div
                style={{
                  width: '100%',
                  height: `${(elemento.grosor / CANVAS_ANCHO_DISENO_PX) * 100}cqw`,
                  background: elemento.color,
                }}
              />
            ) : elemento.tipo === 'rectangulo' ? (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  boxSizing: 'border-box',
                  background: elemento.color_relleno || 'transparent',
                  border: elemento.color_borde
                    ? `${(elemento.grosor_borde / CANVAS_ANCHO_DISENO_PX) * 100}cqw solid ${elemento.color_borde}`
                    : 'none',
                  borderRadius: `${(elemento.radio_esquina / CANVAS_ANCHO_DISENO_PX) * 100}cqw`,
                }}
              />
            ) : elemento.tipo === 'circulo' ? (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  boxSizing: 'border-box',
                  background: elemento.color_relleno || 'transparent',
                  border: elemento.color_borde
                    ? `${(elemento.grosor_borde / CANVAS_ANCHO_DISENO_PX) * 100}cqw solid ${elemento.color_borde}`
                    : 'none',
                  borderRadius: '50%',
                }}
              />
            ) : elemento.tipo === 'qr' ? (
              <QRCodeSVG
                value={resolverContenidoQR(elemento, contribuyente) || ' '}
                size={256}
                fgColor={elemento.color_qr}
                bgColor={elemento.color_fondo}
                marginSize={2}
                style={{ width: '100%', height: '100%' }}
              />
            ) : (
              (() => {
                const estiloTexto = {
                  textAlign: ALINEACION_A_TEXT_ALIGN[elemento.alineacion] || 'left',
                  color: elemento.color,
                  // 800, no 700: ver comentario en DisenadorEditor.jsx / la
                  // fuente Inter solo tenía 400/500/600 cargados.
                  fontWeight: elemento.negrita ? 800 : 600,
                  fontStyle: elemento.cursiva ? 'italic' : 'normal',
                };
                if (elemento.fuente_dato === 'clases' && formatoDeElemento(elemento) === FORMATO_CODIGO_DESCRIPCION) {
                  return (
                    <TextoClasesConDescripcion
                      elemento={elemento}
                      clases={contribuyente.clases}
                      catalogo={catalogo}
                      estilo={estiloTexto}
                    />
                  );
                }
                return (
                  <span className="license-layout-elemento-texto" style={estiloTexto}>
                    {resolverValorLayout(elemento, contribuyente)}
                  </span>
                );
              })()
            )}
          </div>
        ))}
      </div>
    );
  }

  // Inalcanzable: los 3 casos de arriba (a/b/c) son exhaustivos. Se deja por
  // seguridad de tipos.
  return null;
};

export default LicenseCard;
